import { useMutation, useQueryClient, QueryClient } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { sendToApi } from 'api/client'
import { buildErrorMessage } from 'utils'
import {
  InformalFeedbackRequest,
  InformalFeedbackReviewReviewer,
  InformalFeedbackReviewEmailReviewer,
  InformalFeedbackReviewChannelReviewer,
} from 'api/clients/InformalFeedbackRequestsClient'
import { informalFeedbackRequestsKeys } from './informalFeedbackRequestsKeys'
import { useStore } from 'react-redux'
import { upsertInformalFeedbackRequest } from '../../../redux/actions/informalFeedbackRequests'
import { upsertReview } from '../../../redux/actions/reviews'

interface Attributes {
  ends_on?: string
  cleanup_dismissed?: boolean
  add_members?: boolean
  reviewers?: InformalFeedbackReviewReviewer[]
  email_reviewers?: InformalFeedbackReviewEmailReviewer[]
  channel_reviewers?: InformalFeedbackReviewChannelReviewer[]
  viewers?: number[]
  email_viewers?: string[]
  channel_viewers?: number[]
}

type CacheData = { informal_feedback_request: InformalFeedbackRequest } | undefined

export const useUpdateInformalFeedbackRequest = (id: number) => {
  const queryClient = useQueryClient()
  const store = useStore()

  return useMutation<{ informal_feedback_request: InformalFeedbackRequest }, Error, Attributes>(
    informalFeedbackRequestsKeys.update(id),
    async ({
      ends_on,
      cleanup_dismissed,
      add_members,
      reviewers,
      email_reviewers,
      channel_reviewers,
      viewers,
      email_viewers,
      channel_viewers,
    }) => {
      const response = await sendToApi(`/informal_feedback_requests/${id}`, {
        method: 'PATCH',
        body: {
          ends_on,
          cleanup_dismissed,
          add_members,
          reviewers,
          email_reviewers,
          channel_reviewers,
          viewers,
          email_viewers,
          channel_viewers,
        },
      })

      if (response.type !== SUCCESS_RESPONSE_TYPE) {
        throw new Error(
          buildErrorMessage(response.data.errors) ||
            'An error occurred! Our team has been notified and we are looking into it.',
        )
      }
      return response.data
    },
    {
      onMutate: (variables) => {
        const currentRequest = queryClient.getQueryData<CacheData>(
          informalFeedbackRequestsKeys.detail(id),
        )?.informal_feedback_request
        // Optimistic update
        updateCache<Attributes>(queryClient, id, variables)
        return currentRequest
      },
      onError: (_error, _variables, oldAttributes) => {
        const attributes = oldAttributes as Attributes
        updateCache<Attributes>(queryClient, id, { ends_on: attributes.ends_on })
      },
      onSuccess: ({ informal_feedback_request }) => {
        queryClient.invalidateQueries([
          informalFeedbackRequestsKeys.prefix,
          informal_feedback_request.article?.key || informal_feedback_request.design?.key,
        ])
        store.dispatch(upsertInformalFeedbackRequest(informal_feedback_request))
        store.dispatch(upsertReview(informal_feedback_request))
      },
    },
  )
}

export const updateCache = <T>(queryClient: QueryClient, id: number, attributes: T) => {
  queryClient.setQueryData<{ informal_feedback_request: InformalFeedbackRequest } | undefined>(
    informalFeedbackRequestsKeys.detail(id),
    (old) => {
      if (!old) {
        return old
      }
      return { informal_feedback_request: { ...old.informal_feedback_request, ...(attributes as T) } }
    },
  )
}
